(() => {
  if (customElements.get('st-artwork-page')) return;
  const allowedTypes = ['graphic only','graphic and hardware combo'];
  class ArtworkPage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const signal = this.controller.signal;
      this.form = this.querySelector('[data-upload-form]'); this.file = this.querySelector('[data-file]');
      this.error = this.querySelector('[data-error]'); this.quantity = this.querySelector('[data-quantity]');
      this.submitButton = this.querySelector('[data-submit]'); this.skipButton = this.querySelector('[data-skip]');
      this.form.addEventListener('submit', e => { e.preventDefault(); e.stopImmediatePropagation(); this.submit(false); }, {signal});
      this.skipButton?.addEventListener('click', () => this.submit(true), {signal});
      this.file.addEventListener('change', () => this.validateFile(), {signal});
      this.querySelector('[data-remove]').addEventListener('click', () => {this.file.value='';this.validateFile();}, {signal});
      this.querySelector('[data-replace]').addEventListener('click', () => this.file.click(), {signal});
      this.quantity.addEventListener('input', () => this.update(), {signal});
      this.querySelector('[data-minus]').addEventListener('click', () => {this.quantity.stepDown();this.update();}, {signal});
      this.querySelector('[data-plus]').addEventListener('click', () => {this.quantity.stepUp();this.update();}, {signal});
      this.querySelectorAll('[data-product-link],[data-online-link]').forEach(link => link.addEventListener('click', e => {
        if (link.getAttribute('aria-disabled') === 'true') e.preventDefault();
        else if (this.validFile && !window.confirm('Your selected file will not be kept if you leave this page. Continue?')) e.preventDefault();
      }, {signal}));
      const drop = this.querySelector('[data-drop]');
      drop.addEventListener('dragover', e => {e.preventDefault();if(this.ready&&!this.busy)drop.classList.add('is-over');}, {signal});
      drop.addEventListener('dragleave', () => drop.classList.remove('is-over'), {signal});
      drop.addEventListener('drop', e => {
        e.preventDefault();drop.classList.remove('is-over');if(!this.ready||this.busy)return;
        if(e.dataTransfer.files.length!==1){this.showError('Please upload one JPG or PNG image.');return;}
        try {this.file.files=e.dataTransfer.files;this.validateFile();}catch{this.showError('Please use Browse Files to select your artwork.');}
      }, {signal});
      const guidelines = this.querySelector('dialog');
      this.querySelector('[data-guidelines]').addEventListener('click', () => guidelines.showModal(), {signal});
      this.querySelector('[data-close-guidelines]').addEventListener('click', () => guidelines.close(), {signal});
      window.addEventListener('pageshow', e => {if(e.persisted){this.busy=false;this.submitButton.textContent='Add to Cart';this.file.disabled=!this.ready;this.update();}}, {signal});
      this.load();
    }
    disconnectedCallback(){this.controller?.abort();if(this.previewURL)URL.revokeObjectURL(this.previewURL);this.controller=null;}
    showError(message){this.error.textContent=message;}
    parseContext() {
      const p=new URLSearchParams(location.search);
      this.handle=p.get('product')||'';this.variantId=p.get('variant')||'';
      if(!/^[a-z0-9][a-z0-9-]*$/i.test(this.handle)||!/^\d+$/.test(this.variantId))throw Error('Please open this page from a product: choose your type and size, click Customize, then Upload Artwork.');
      this.initialQuantity=Number(p.get('quantity')||1);
      if(!Number.isSafeInteger(this.initialQuantity)||this.initialQuantity<1)throw Error('The quantity is invalid. Please return to the product and choose your quantity again.');
      this.context=null;
      if(p.has('context')){
        try{this.context=JSON.parse(sessionStorage.getItem('st-artwork:'+p.get('context'))||'null');}catch{}
        if(!this.context||this.context.handle!==this.handle||String(this.context.variant)!==this.variantId||!Number.isFinite(this.context.created)||Date.now()-this.context.created>2*60*60*1000)throw Error('Your saved selection has expired or is not available in this tab. Please return to the product and choose Upload Artwork again.');
      }
    }
    productURL(){return new URL(this.dataset.root+'products/'+encodeURIComponent(this.handle),location.origin);}
    async getProduct(){
      const url=this.productURL();url.pathname+='.js';
      const response=await fetch(url,{credentials:'same-origin',cache:'no-store',signal:this.controller.signal});
      if(!response.ok)throw Error('We could not load this product. Please return to the product page and try again.');
      return response.json();
    }
    resolveVariant(product){
      const variant=product.variants?.find(v=>String(v.id)===this.variantId);
      if(!variant)throw Error('This product option could not be found. Please return to the product and select it again.');
      if(!variant.available)throw Error('This product option is currently unavailable. Please return to the product and select another option.');
      const index=product.options?.findIndex(o=>String(typeof o==='string'?o:o.name).trim().toLowerCase()==='type');
      const type=String(variant.options?.[index]||'').trim().toLowerCase();
      if(!allowedTypes.includes(type))throw Error('Please choose Graphic Only or Graphic and Hardware Combo on the product page.');
      return {variant,type};
    }
    async load(){
      try{
        this.parseContext();
        this.updateLinks();
        const product=await this.getProduct();
        const {variant,type}=this.resolveVariant(product);
        this.product=product;this.variant=variant;this.type=type;
        this.quantity.value=this.initialQuantity;
        const rules=variant.quantity_rule||this.context?.quantityRule;
        if(rules){this.quantity.min=String(rules.min||1);this.quantity.step=String(rules.increment||1);if(rules.max)this.quantity.max=String(rules.max);}
        this.querySelector('[data-title]').textContent=product.title;
        this.querySelector('[data-bottom-title]').textContent=product.title;
        this.querySelector('[data-variant]').textContent=variant.options.join(' · ');
        this.querySelector('[data-bottom-variant]').textContent=variant.options.join(' · ');
        this.querySelector('[data-includes]').textContent=type==='graphic and hardware combo'?'✓ Printed graphic + stand included in the selected variant.':'✓ Printed graphic only — stand not included.';
        this.ready=true;this.quantity.disabled=false;this.file.disabled=false;
        this.querySelector('[data-plus]').disabled=false;this.querySelector('[data-minus]').disabled=false;
        this.update();
      }catch(error){if(error.name!=='AbortError'){this.showError(error.message);this.querySelector('[data-variant]').textContent='Select a product to get started.';}}
    }
    updateLinks(){
      if(!this.handle||!this.variantId)return;
      const url=this.productURL();url.searchParams.set('variant',this.variantId);
      const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);
      this.querySelectorAll('[data-product-link]').forEach(a=>a.href=url.href);
      url.searchParams.set('st_design','online');url.searchParams.set('quantity',String(this.quantity?.value||this.initialQuantity||1));
      this.querySelector('[data-online-link]').href=url.href;this.querySelector('[data-online-link]').removeAttribute('aria-disabled');
    }
    update(){
      const q=Number(this.quantity.value),validQuantity=Number.isSafeInteger(q)&&q>0&&this.quantity.checkValidity();
      this.file.disabled=!this.ready||this.busy;
      this.querySelector('[data-replace]').disabled=!!this.busy;
      this.querySelector('[data-remove]').disabled=!!this.busy;
      this.submitButton.disabled=!this.ready||this.busy||!this.validFile||!validQuantity;
      if(this.skipButton)this.skipButton.disabled=!this.ready||this.busy||!validQuantity;
      if(this.variant){
        const money=new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:this.dataset.currency});
        this.querySelector('[data-total]').textContent=validQuantity?money.format(this.variant.price*q/100):'—';
        this.querySelector('[data-unit]').textContent=money.format(this.variant.price/100)+' each';
        this.updateLinks();
      }
    }
    async validateFile(){
      const file=this.file.files[0];this.validFile=false;this.showError('');this.querySelector('[data-preview]').hidden=true;
      if(this.previewURL)URL.revokeObjectURL(this.previewURL);this.previewURL=null;this.update();
      if(!file)return;
      if(!/\.(jpe?g|png)$/i.test(file.name)||!['image/jpeg','image/png',''].includes(file.type)){this.showError('Please choose a JPG or PNG image.');this.file.value='';return;}
      if(!file.size||file.size>10*1024*1024){this.showError('Please choose a non-empty image no larger than 10 MB.');this.file.value='';return;}
      const url=URL.createObjectURL(file);this.previewURL=url;const img=new Image();img.src=url;
      try{await img.decode();}catch{if(this.previewURL===url){this.showError('This image could not be opened. Please choose another JPG or PNG.');this.file.value='';}return;}
      if(this.previewURL!==url||this.file.files[0]!==file)return;
      this.querySelector('[data-preview-image]').src=url;this.querySelector('[data-filename]').textContent=file.name;
      this.querySelector('[data-fileinfo]').textContent=`${img.naturalWidth} × ${img.naturalHeight} px · ${(file.size/1024/1024).toFixed(2)} MB`;
      this.querySelector('[data-preview]').hidden=false;this.validFile=true;this.update();
    }
    async submit(later){
      if(this.busy||!this.ready)return;
      if(later&&this.dataset.allowLater!=='true')return;
      this.showError('');
      const quantity=Number(this.quantity.value);
      if(!Number.isSafeInteger(quantity)||quantity<1||!this.quantity.reportValidity()){this.showError('Please enter a valid quantity.');return;}
      if(!later&&(!this.validFile||!this.form.reportValidity())){this.showError('Please select a valid JPG or PNG image.');return;}
      this.busy=true;this.update();this.quantity.disabled=true;this.querySelector('[data-minus]').disabled=true;this.querySelector('[data-plus]').disabled=true;
      this.submitButton.textContent=later?'Adding…':'Uploading…';
      try{
        const product=await this.getProduct();const {variant}=this.resolveVariant(product);
        // Refresh availability/price before posting; Shopify determines the charged price.
        if(variant.price!==this.variant.price){this.variant=variant;throw Error('The product price has changed. Please review the updated total and add it again.');}
        this.form.elements.namedItem('id').value=variant.id;
        this.form.elements.namedItem('quantity').value=quantity;
        this.form.elements.namedItem('properties[_st_artwork_id]').value=crypto.randomUUID();
        this.form.elements.namedItem('properties[Design method]').value=later?'Artwork to follow':'Uploaded artwork';
        this.form.elements.namedItem('properties[Artwork status]').value=later?'Awaiting artwork':'Uploaded';
        this.file.disabled=later;
        this.form.querySelectorAll('[data-context-property]').forEach(f=>f.remove());
        for(const pair of this.context?.properties||[]){
          if(!Array.isArray(pair)||pair.length!==2)continue;
          const [name,value]=pair;
          if(typeof name!=='string'||typeof value!=='string'||!/^properties\[[^\]]+\]$/.test(name)||name.startsWith('properties[_')||['properties[Artwork]','properties[Artwork status]','properties[Design method]','properties[Artwork notes]'].includes(name))continue;
          const field=document.createElement('input');field.type='hidden';field.name=name;field.value=value;field.dataset.contextProperty='';this.form.append(field);
        }
        HTMLFormElement.prototype.submit.call(this.form);
      }catch(error){
        this.busy=false;this.file.disabled=false;this.quantity.disabled=false;this.querySelector('[data-minus]').disabled=false;this.querySelector('[data-plus]').disabled=false;
        this.submitButton.textContent='Add to Cart';this.update();if(error.name!=='AbortError')this.showError(error.message);
      }
    }
  }
  customElements.define('st-artwork-page',ArtworkPage);
})();
